import { Classes, Icon } from '@blueprintjs/core';
import { SearchResultWithMetadata } from '@retro-ui/api-contract';
import { SearchRequest } from '@retro-ui/prisma';
import cx from 'classnames';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';

import { apiClient } from '@f/api-client';
import LoadingScreen from '@f/components/shared/loading-screen/LoadingScreen';
import Toolbar from '@f/components/shared/toolbar/Toolbar';
import { findReactionSearchPreferences } from '@f/services/reaction-search-preference/reaction-search-preference.actions';
import { TargetValidationProvider } from '@f/context';

import { CreateBatchSearchForm, RerunBatch } from './components/CreateBatchSearchForm';

export function CreateBatchSearchPage() {
  const location = useLocation();
  const dispatch = useDispatch();

  const isLoadingPresets = useSelector(
    (state: any) =>
      Object.keys(state.reactionSearchPreference.data).length === 0 ||
      state.reactionSearchPreference.find.processing > 0
  );

  const [isFetchingBatch, setIsFetchingBatch] = useState(false);
  const [rerunBatch, setRerunBatch] = useState<SearchRequest | null>(null);

  const [isFetchingBatchResults, setIsFetchingBatchResults] = useState(false);
  const [rerunBatchResults, setRerunBatchResults] = useState<SearchResultWithMetadata[] | null>(null);

  const fetchBatchForRerun = async (batchId: string | null) => {
    if (!batchId) {
      setRerunBatch(null);
      return;
    }
    setIsFetchingBatch(true);
    try {
      const { status, body } = await apiClient.search.fetchUniqueSearch({
        params: {
          searchId: batchId,
        },
      });

      if (status !== 200) {
        throw new Error('Failed to fetch search that should be re-run');
      }

      setRerunBatch(body);
    } catch (err) {
      setRerunBatch(null);
    } finally {
      setIsFetchingBatch(false);
    }
  };

  const fetchBatchResultsForRerun = async (batchId: string | null) => {
    if (!batchId) {
      setRerunBatchResults(null);
      return;
    }
    setIsFetchingBatchResults(true);
    try {
      const { status, body } = await apiClient.search.fetchSearchResults({
        params: {
          searchId: batchId,
        },
      });

      if (status !== 200) {
        throw new Error('Failed to fetch reasults of search that should be re-run');
      }

      setRerunBatchResults(body.data);
    } catch (err) {
      setRerunBatchResults(null);
    } finally {
      setIsFetchingBatchResults(false);
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const batchId = searchParams.get('rerun');

    if (batchId !== rerunBatch?.id) {
      fetchBatchForRerun(batchId);
      fetchBatchResultsForRerun(batchId);
    }
  }, [location]);

  useEffect(() => {
    dispatch(findReactionSearchPreferences());
  }, []);

  if (isFetchingBatch) {
    return <LoadingScreen caption="Loading search to re-run..." />;
  }

  if (isLoadingPresets) {
    return <LoadingScreen caption="Loading parameter presets..." />;
  }

  if (isFetchingBatchResults) {
    return <LoadingScreen caption="Loading search results to re-run..." />;
  }

  return (
    <TargetValidationProvider>
      <Toolbar>
        <Toolbar.Item>
          <NavLink className={cx(Classes.BUTTON, Classes.MINIMAL)} to="/dashboard/batch">
            <Icon icon="double-chevron-left" />
            <span className={Classes.BUTTON_TEXT}>Back to searches</span>
          </NavLink>
        </Toolbar.Item>
        <Toolbar.Title>{rerunBatch ? `Re-run a batch` : 'Design a new search'}</Toolbar.Title>
      </Toolbar>

      <CreateBatchSearchForm
        rerunBatch={rerunBatch as RerunBatch}
        rerunBatchResults={rerunBatchResults}
        key={rerunBatch?.id}
      />
    </TargetValidationProvider>
  );
}
